import { useState } from 'react';
import { HelpCircle, ChevronRight, TrendingUp, TrendingDown, X, Sparkles, Info } from 'lucide-react';
import { FUNNEL_CARD_DEFS } from '../constants';

// Hero metric row (UPM-64, reordered UPM-150): three cards leading with
// Primary Recommendation, then Top 5, then Mention Rate. Primary Rec keeps the
// highlight. #1 on List stays in the payload and the card definitions but has
// no surface here. Connectors are directional only: these three metrics are
// measured independently, so no conversion percentage is shown between cards.

export interface HeroStat { value: string; label: string; sub: string; raw: number }
export interface PerModelFunnelRow { name: string; mention: number; top5: number; num1: number; primary: number }
export type HeroCardKey = 'mention' | 'top5' | 'primary' | 'balanced';

// The stats ARRAY order is the payload contract (buildFunnelStatsFromApi):
// 0 mention, 1 top5, 2 num1, 3 primary. It is unchanged.
//
// The RENDER order is the product's funnel order and is fixed everywhere
// (UPM-150): Primary Recommendation, Top 5, Mention Rate. Primary
// recommendation leads and keeps the highlight. Performance already complied;
// this is what brings Deep Signal and the Brand Monitoring hero that reuses it
// into line. Do not re-sort these.
const CARDS: { key: Exclude<HeroCardKey, 'balanced'>; statIdx: number; defIdx: number }[] = [
  { key: 'primary', statIdx: 3, defIdx: 3 },
  { key: 'top5',    statIdx: 1, defIdx: 1 },
  { key: 'mention', statIdx: 0, defIdx: 0 },
];

export function HeroMetricsRow({
  stats,
  baselineStats,
  isBaselineRun,
  question,
  runChipLabel,
  comparisonLabel = 'since last measurement',
  heroCard = 'primary',
  excludedLanes = [],
  onAskAI,
}: {
  stats: HeroStat[];
  baselineStats: HeroStat[] | null;
  isBaselineRun: boolean;
  question: string;
  runChipLabel: string;
  // Names the delta's comparison, e.g. "vs baseline, May 23", so the number
  // states what it's measured against. Defaults to the old copy for safety.
  comparisonLabel?: string;
  heroCard?: HeroCardKey;
  // Lanes this client measured that are NOT counted in the numbers on these
  // cards, because they were added after their baseline. Rendering them here is
  // not decoration: the lane is visible elsewhere on the page with its own
  // column, so without this the client sees a model being measured and a
  // headline that ignores it, with nothing connecting the two.
  excludedLanes?: { id: string; label: string }[];
  onAskAI: (text: string) => void;
}) {
  const [openModal, setOpenModal] = useState<number | null>(null); // defIdx
  const [showExcluded, setShowExcluded] = useState(false);
  const afterMode = !isBaselineRun && !!baselineStats;

  // FUNNEL_CARD_DEFS and the stats array share one order (mention, top5,
  // num1, primary), so the def index doubles as the stat index.
  const modalDef = openModal !== null ? FUNNEL_CARD_DEFS[openModal] : null;
  const modalStat = openModal !== null ? stats[openModal] : null;

  return (
    <div style={{
      backgroundColor: 'var(--background)',
      border: '1px solid var(--border)',
      borderRadius: '12px',
      boxShadow: 'var(--elevation-sm)',
      padding: '28px 30px',
    }}>
      {/* Header: eyebrow + question left, run chip right */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <div>
          <p style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--muted-foreground)', marginBottom: '4px' }}>
            Key Business Metrics
          </p>
          <h3 style={{ fontSize: '28px', fontWeight: 600, color: 'var(--foreground)', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            {question}
          </h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', position: 'relative' }}>
          {/* One affordance for the whole row rather than one per card: all
              three numbers are blended over the same lane set, so three
              identical buttons would say the same thing three times. */}
          {excludedLanes.length > 0 && (
            <>
              <button
                onClick={() => setShowExcluded(v => !v)}
                title="Which models are not counted in these numbers?"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  border: '1px solid var(--border)', borderRadius: '8px', padding: '7px 12px',
                  backgroundColor: 'var(--background)', fontSize: '13px', fontWeight: 600,
                  color: 'var(--muted-foreground)', whiteSpace: 'nowrap', cursor: 'pointer',
                }}
              >
                <Info size={14} />
                {excludedLanes.length === 1
                  ? '1 model not counted'
                  : `${excludedLanes.length} models not counted`}
              </button>

              {showExcluded && (
                <div
                  style={{
                    position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 20,
                    width: '320px', padding: '16px',
                    backgroundColor: 'var(--card)', border: '1px solid var(--border)',
                    borderRadius: '10px', boxShadow: '0 12px 32px rgba(0,0,0,0.16)',
                    textAlign: 'left', whiteSpace: 'normal',
                  }}
                >
                  <p style={{ fontSize: '13px', fontWeight: 700, color: 'var(--foreground)', marginBottom: '8px' }}>
                    Not counted in these numbers
                  </p>
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 10px' }}>
                    {excludedLanes.map(lane => (
                      <li
                        key={lane.id}
                        style={{ fontSize: '13px', fontWeight: 600, color: 'var(--foreground)', padding: '3px 0' }}
                      >
                        {lane.label}
                      </li>
                    ))}
                  </ul>
                  <p style={{ fontSize: '12px', color: 'var(--muted-foreground)', lineHeight: 1.55 }}>
                    {excludedLanes.length === 1 ? 'This model was' : 'These models were'} added
                    after your baseline measurement.{' '}
                    {excludedLanes.length === 1 ? 'It is' : 'They are'} measured and reported as
                    {excludedLanes.length === 1 ? ' its' : ' their'} own lane, but kept out of the
                    blended figures above so your trend stays a like-for-like comparison.
                  </p>
                </div>
              )}
            </>
          )}

          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            border: '1px solid var(--border)', borderRadius: '8px', padding: '7px 12px',
            backgroundColor: 'var(--background)', fontSize: '13px', fontWeight: 600,
            color: 'var(--foreground)', whiteSpace: 'nowrap',
          }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '9999px', backgroundColor: 'var(--alpha-teal)', flexShrink: 0 }} />
            {runChipLabel}
          </span>
        </div>
      </div>

      {/* Card row: three cards + directional chevrons. Wraps below iPad width;
          chevrons hide when a card has wrapped off the shared row. */}
      <div style={{ display: 'flex', alignItems: 'stretch', flexWrap: 'wrap', rowGap: '14px' }}>
        {CARDS.map((c, i) => {
          const s = stats[c.statIdx];
          const isHero = heroCard === c.key;
          const base = baselineStats?.[c.statIdx];
          const delta = afterMode && base ? (s.raw - base.raw) * 100 : null;
          const negative = delta !== null && delta < -0.05;
          const positive = delta !== null && delta > 0.05;
          const DeltaIcon = negative ? TrendingDown : TrendingUp;
          const deltaColor = negative ? 'var(--infrared-coral)' : 'var(--signal-green)';

          const card = (
            <div
              key={c.key}
              style={{
                flex: '1 1 0',
                minWidth: '176px',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '12px',
                padding: '18px 18px 16px',
                backgroundColor: isHero ? 'var(--alpha-teal-5)' : 'var(--card)',
                border: `1px solid ${isHero ? 'var(--alpha-teal-20)' : 'var(--border)'}`,
                boxShadow: isHero ? 'var(--shadow-accent-ring), var(--elevation-md)' : 'var(--elevation-sm)',
                overflow: 'hidden',
              }}
            >
              {isHero && (
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', backgroundColor: 'var(--alpha-teal)', borderRadius: '12px 12px 0 0' }} />
              )}

              {/* Label block + info affordance */}
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', marginBottom: '14px' }}>
                <div>
                  <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--foreground)', lineHeight: 1.2 }}>{s.label}</p>
                  <p style={{ fontSize: '12px', color: 'var(--muted-foreground)', marginTop: '2px' }}>{s.sub}</p>
                </div>
                <button
                  onClick={() => setOpenModal(c.defIdx)}
                  title="How is this calculated?"
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: isHero ? 'var(--alpha-teal)' : 'var(--silver-mist)', display: 'flex', flexShrink: 0 }}
                >
                  <HelpCircle size={15} />
                </button>
              </div>

              {/* Value */}
              <p style={{
                fontSize: isHero ? '48px' : '42px',
                fontWeight: 700,
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
                letterSpacing: '-0.02em',
                color: isHero ? 'var(--alpha-teal)' : 'var(--foreground)',
              }}>
                {s.value}
              </p>

              {/* Comparison slot: always present so the card floor never shifts */}
              <div style={{ marginTop: 'auto', paddingTop: '16px', minHeight: '22px' }}>
                {delta === null ? (
                  <span style={{ fontSize: '12px', color: 'var(--muted-foreground)' }}>First measurement</span>
                ) : (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: isHero ? '14px' : '13px', fontWeight: 700, color: positive || negative ? deltaColor : 'var(--muted-foreground)', fontVariantNumeric: 'tabular-nums' }}>
                    <DeltaIcon size={isHero ? 14 : 13} />
                    {`${delta > 0 ? '+' : delta < 0 ? '-' : ''}${Math.abs(delta).toFixed(1)} pts`}
                    <span style={{ fontWeight: 500, color: 'var(--muted-foreground)' }}>{comparisonLabel}</span>
                  </span>
                )}
              </div>
            </div>
          );

          if (i === CARDS.length - 1) return card;
          return [
            card,
            <div key={`conn-${i}`} style={{ flex: '0 0 40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ChevronRight size={20} style={{ color: 'var(--silver-mist)' }} />
            </div>,
          ];
        })}
      </div>

      {/* "How is this calculated?" explainer modal (existing content defs) */}
      {modalDef && modalStat && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.45)' }}
          onClick={() => setOpenModal(null)}
        >
          <div
            style={{
              backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '12px',
              padding: '32px', maxWidth: '520px', width: '90%', maxHeight: '80vh', overflowY: 'auto',
              position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setOpenModal(null)}
              style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--muted-foreground)', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--muted)'; e.currentTarget.style.color = 'var(--foreground)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--muted-foreground)'; }}
            >
              <X size={18} />
            </button>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--foreground)', marginBottom: '6px' }}>{modalStat.label}</h3>
              <p style={{ fontSize: 'var(--text-base)', color: 'var(--muted-foreground)', lineHeight: '1.55' }}>{modalDef.definition}</p>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '4px', height: '16px', borderRadius: '99px', backgroundColor: '#28a9d2', flexShrink: 0 }} />
                <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)' }}>How it's calculated</h4>
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.6' }}>{modalDef.calculation}</p>
            </div>

            <div style={{ marginBottom: '18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '4px', height: '16px', borderRadius: '99px', backgroundColor: '#00c9c4', flexShrink: 0 }} />
                <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)' }}>How to interpret this</h4>
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.6' }}>{modalDef.interpretation}</p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '4px', height: '16px', borderRadius: '99px', backgroundColor: '#12b76a', flexShrink: 0 }} />
                <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--foreground)' }}>What's a good score?</h4>
              </div>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', lineHeight: '1.6' }}>{modalDef.goodRange}</p>
            </div>

            <button
              onClick={() => {
                onAskAI(modalDef.aiQuestion.replace('{value}', modalStat.value));
                setOpenModal(null);
              }}
              style={{
                width: '100%', padding: '12px 20px', backgroundColor: '#7e5fe0', border: 'none',
                borderRadius: '8px', fontSize: 'var(--text-sm)', fontWeight: 600, color: '#fff',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <Sparkles size={15} />
              Continue this conversation with AI &#8594;
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
